import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useMutation, useQuery } from '@apollo/client';
import { Avatar, Box, Button, Divider, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { Search as SearchIcon, Send as SendIcon } from '@mui/icons-material';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';
import { CLIENT_PROGRESS_OBSERVATIONS, ADD_PROGRESS_OBSERVATION } from 'api/graphql/physicalProgress';

/**
 * Observaciones del entrenador sobre un cliente. Varios entrenadores pueden anotar; se listan de la
 * más reciente a la más antigua, con buscador. El backend notifica al cliente al crearse.
 * `readOnly` (vista del cliente en su "Mi progreso"): solo lee, sin caja para agregar.
 */
function ProgressObservations({ clientId, readOnly = false }) {
  const dispatch = useDispatch();
  const { data, refetch } = useQuery(CLIENT_PROGRESS_OBSERVATIONS, { variables: { clientId: String(clientId) }, fetchPolicy: 'cache-and-network', skip: !clientId });
  const [addObs, { loading }] = useMutation(ADD_PROGRESS_OBSERVATION);
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');

  const observations = data?.clientProgressObservations ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return observations;
    return observations.filter((o) => (o.body || '').toLowerCase().includes(q) || (o.trainer?.full_name || '').toLowerCase().includes(q));
  }, [observations, search]);

  const submit = async () => {
    const body = draft.trim();
    if (!body) return;
    try {
      await addObs({ variables: { clientId: String(clientId), body } });
      setDraft('');
      dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `obs-${Date.now()}`, variant: 'success', message: 'Observación guardada' })));
      refetch();
    } catch { /* errorHandler */ }
  };

  // En la vista del cliente (solo lectura) no mostramos la sección si aún no hay nada.
  if (readOnly && observations.length === 0) return null;

  return (
    <Box sx={{ px: { xs: 0, sm: 2 }, pb: 4, pt: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>
        {readOnly ? 'Observaciones de tus entrenadores' : 'Observaciones'}
      </Typography>
      <Box sx={{ backgroundColor: 'surface.content', border: '1px solid', borderColor: 'hairline.main', borderRadius: '16px', p: { xs: 2, md: 2.5 } }}>
        {!readOnly && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
            <TextField fullWidth size="small" multiline minRows={1} placeholder="Anota algo sobre este cliente…" value={draft} onChange={(e) => setDraft(e.target.value)} inputProps={{ maxLength: 1000 }} />
            <Button variant="contained" color="primary" startIcon={<SendIcon fontSize="small" />} onClick={submit} disabled={!draft.trim() || loading} sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
              Agregar
            </Button>
          </Stack>
        )}

        {observations.length > 0 && (
          <TextField
            fullWidth size="small" placeholder="Buscar por texto o entrenador…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ mb: 2 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment> }}
          />
        )}

        {filtered.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
            {observations.length === 0 ? 'Aún no hay observaciones.' : 'Sin resultados.'}
          </Typography>
        ) : filtered.map((o, idx) => (
          <Box key={o.id}>
            {idx > 0 && <Divider sx={{ borderColor: 'hairline.main', my: 1.5 }} />}
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Avatar src={o.trainer?.photo} alt={o.trainer?.full_name} sx={{ width: 30, height: 30, fontSize: '0.75rem' }}>{o.trainer?.full_name?.[0]}</Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{o.trainer?.full_name}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>{(o.created_at || '').slice(0, 10)}</Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>{o.body}</Typography>
              </Box>
            </Stack>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

ProgressObservations.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  readOnly: PropTypes.bool
};

export default ProgressObservations;
